import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import TextInput from '../../components/NumberTextInput';
import Modal from '../../components/modal/Modal';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../../components/SvgIcon';
import { colors } from '../../lib/colors';
import dayjs from 'dayjs';
import NewCalendar from '../../components/Calendar/NewCalendar';
import { MemberMembershipDataType, UnpaidPaymentTableType } from '../../services/useMembershipQueryService';
import useMembershipPaymentsMutationService from '../../services/useMembershipPaymentsMutationService';
import {
    getSharedAccountingErrorMessage,
    resolveSharePoolAdminError,
} from '../../features/sharedMembership/errors';
import { useQueryClient } from '@tanstack/react-query';
import { useMembershipTheme } from '../../components/MemberProfile/useMembershipTheme';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    unpaidRowData?: UnpaidPaymentTableType;
    onPostSplitPaymentSuccess?: () => void;
    membership?: MemberMembershipDataType;
    coachName?: string;
    memberName?: string;
    createAlert?: (title: string, content: string, isError: boolean) => void;
};

const ModalPartialPayment = ({
    setIsVisible,
    unpaidRowData,
    onPostSplitPaymentSuccess,
    membership,
    coachName: _coachName,
    memberName: _memberName,
    createAlert,
}: Props) => {
    const queryClient = useQueryClient();
    const { postSplitPaymentMutation } = useMembershipPaymentsMutationService({
        onPostSplitPaymentSuccess: onPostSplitPaymentSuccess,
    });
    const membershipId = membership ? membership._id : unpaidRowData?.membershipId || '';
    const coachName = membership ? _coachName : unpaidRowData?.coachName || '';
    const memberName = membership ? _memberName : unpaidRowData?.memberName || '';
    const membershipName = membership ? membership.name : unpaidRowData?.name || '';
    // 수강권 테마 — 두 입력(membership / unpaidRowData) 중 존재하는 쪽을 훅에 위임
    // 두 타입 모두 status/source/BGColor 필드를 가지고 있어 구조적 타이핑으로 통일 처리됨
    const themeInput = membership ?? unpaidRowData;
    const { headerBackground, isGroup } = useMembershipTheme(themeInput);
    const authObject = getItemWithExpiration('authObject');
    const centerId = authObject?.centerId;

    // useMemo로 감싸서 useEffect dep array가 매 렌더마다 새로 만들어지는 걸 방지
    const splitPaymentExtraParams = useMemo(
        () => ({
            ...(isGroup ? { source: 'group' as const, centerId } : {}),
        }),
        [isGroup, centerId],
    );

    /**
     * 판매가, 결제금액, 미수금액.
     *
     * 인계된 수강권은 결제 원장이 원판매 수강권에 남아 자기 값만 보면 상세 헤더와 숫자가
     * 어긋난다(헤더는 계보 합계). 담당강사만 바뀐 하나의 수강권이므로 미수금도 하나여야 한다.
     * 서버가 계보 값을 내려주면 그걸 쓰고, 없으면(인계 이력 없음) 기존 값 그대로다.
     * 서버도 결제를 계보 각 세대의 미수금에 원판매부터 순서대로 배분한다.
     */
    const chain = membership as any;
    const hasChainAmounts = chain?.chainTotalPaymentAmount !== undefined;
    const totalPaymentAmount =
        (hasChainAmounts
            ? chain.chainTotalPaymentAmount
            : membership
              ? membership.totalPaymentAmount
              : unpaidRowData?.totalPaymentAmount) || 0;
    const totalPrice =
        (hasChainAmounts ? chain.chainTotalPrice : membership ? membership.totalPrice : unpaidRowData?.totalPrice) ||
        0;
    const totalUnpaidAmount =
        (hasChainAmounts
            ? chain.chainTotalUnpaidAmount
            : membership
              ? membership.totalUnpaidAmount
              : unpaidRowData?.totalUnpaidAmount) || 0;

    // 분할결제 금액, 분할 결제일시
    const [partialPrice, setPartialPrice] = useState(0);
    const [partialDate, setPartialDate] = useState(dayjs().toISOString());

    const [isPriceError, setIsPriceError] = useState(false);
    const enterResubmitLockedRef = React.useRef(false);
    const divRef = React.useRef<HTMLDivElement>(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [divPosition, setDivPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (divRef.current) {
            const rect = divRef.current.getBoundingClientRect();
            setDivPosition({ top: rect.top + rect.height, left: rect.left });
        }
    }, [divRef]);

    /**
     * 응답을 보기 전에 모달을 닫으면 실패(회계·예약 경합 409 등)를 알릴 곳이 사라져
     * 관리자는 수납이 기록된 줄 알게 된다. 성공을 확인한 뒤에만 닫는다.
     */
    const submitPartialPayment = useCallback(async () => {
        if (isPriceError || !partialPrice || postSplitPaymentMutation.isLoading) return;

        const result = await postSplitPaymentMutation
            .mutateAsync({
                membershipId,
                amount: partialPrice,
                date: partialDate,
                ...splitPaymentExtraParams,
            })
            .catch(() => null);

        // PT: 201, 그룹: 200
        if (result?.status === 200 || result?.status === 201) {
            setIsVisible(false);
            return;
        }

        const resolved = resolveSharePoolAdminError(
            result,
            '분할 결제를 처리하지 못했습니다. 잠시 후 다시 시도해주세요.',
        );
        const message = getSharedAccountingErrorMessage(result, resolved.message);
        // 실패 알럿을 Enter로 닫으면 그 keydown이 아래 window 리스너까지 올라가 같은 요청을
        // 한 번 더 보낸다. 분할 수납에는 멱등 키가 없어 매출이 중복 기록되므로 Enter만 잠근다
        // (버튼 클릭은 의도한 재시도라 그대로 통과시킨다).
        enterResubmitLockedRef.current = true;
        if (createAlert) createAlert('분할 결제 실패', message, true);
        else alert(message);
    }, [
        createAlert,
        isPriceError,
        membershipId,
        partialDate,
        partialPrice,
        postSplitPaymentMutation,
        setIsVisible,
        splitPaymentExtraParams,
    ]);

    // 입력을 다시 만지면 재시도 의사가 분명하므로 Enter 잠금을 푼다.
    useEffect(() => {
        enterResubmitLockedRef.current = false;
    }, [partialPrice, partialDate]);

    // 엔터 입력시 분할결제하기
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !enterResubmitLockedRef.current) void submitPartialPayment();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [submitPartialPayment]);

    return (
        <Modal
            setIsVisible={setIsVisible}
            borderRadius={25}
            whiteViewWidth={825}
            isCloseButton
            modalAlign="center"
            isCloseOutside={false}
            modalCommpont={
                isCalendarOpen ? (
                    <NewCalendar
                        date={dayjs(partialDate)}
                        selectedDay={dayjs(partialDate)}
                        setIsCalendar={setIsCalendarOpen}
                        onClickDate={(day) => {
                            setPartialDate(dayjs(day).toISOString());
                        }}
                        isAllDate={false}
                        top={divPosition.top - 250}
                        left={divPosition.left}
                        isNextMonth
                        isPrevMonth
                        titleFormat="YYYY년 MM월"
                    />
                ) : undefined
            }
        >
            <Col className="p-[35px]">
                <Row className="items-center mb-[57px]">
                    <MediumText fontSize={38} lineHeight={45.6} color="#3D4244" className="mr-[31px]">
                        분할 결제
                    </MediumText>
                    <MediumText fontSize={23} lineHeight={33} color="#3D4244" className="mr-[8px]">
                        {coachName} 강사
                    </MediumText>
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={20}
                        fill={colors.trans}
                        stroke="#3D4244"
                        strokeWidth={1.5}
                    />
                    <MediumText fontSize={23} lineHeight={33} color="#3D4244" className="ml-[8px]">
                        {memberName} 회원
                    </MediumText>
                </Row>
                <Row className="flex justify-between w-full items-center mb-[51px]">
                    <Col className="flex flex-col w-full items-center">
                        <DemiLightText fontSize={16} lineHeight={22} color="#3D4244" className="mb-[20px]">
                            현재 수강권 정보
                        </DemiLightText>
                        <Col
                            className="flex flex-col w-full rounded-tl-[12px] rounded-tr-[12px] h-[88px]  justify-center items-center "
                            style={{
                                background: headerBackground,
                                alignItems: 'center',
                            }}
                        >
                            <Col className="flex w-full h-full pt-[16px] pb-[21px] pl-[24px] justify-between items-start">
                                <BoldText fontSize={18} lineHeight={28} color="white">{`${membershipName}`}</BoldText>
                            </Col>
                        </Col>
                        <Col className="flex border border-solid border-[#E5E5E9] pl-[29px] pr-[21px] py-[31px]  rounded-bl-[12px] rounded-br-[12px] h-[250px] w-full">
                            <Row className="flex justify-between w-full mb-[17px]">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    판매가
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(totalPrice)}
                                </RegularText>
                            </Row>

                            <Row className="flex justify-between w-full mb-[17px]">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    결제 금액
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(totalPaymentAmount)}
                                </RegularText>
                            </Row>

                            <Row className="flex justify-between w-full">
                                <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                    미수금액
                                </DemiLightText>
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {convertToKRW(totalUnpaidAmount)}
                                </RegularText>
                            </Row>
                        </Col>
                    </Col>
                    <Col className="flex flex-col w-[1px] h-[375px] bg-[#D9D9D9] mx-[41px]" />
                    <Col className="flex flex-col w-full h-[309px] ">
                        <Col className="mb-[33px]">
                            <Row className="items-center mb-[12px]">
                                <RegularText fontSize={16} lineHeight={23} color="#505967" className=" mr-[12px]">
                                    분할 결제 금액
                                </RegularText>
                                <RegularText
                                    fontSize={13}
                                    lineHeight={18}
                                    color={isPriceError ? 'red' : '#505967'}
                                    letterSpacing={-0.3}
                                >
                                    {isPriceError ? '분할 결제금액이 미수금액을 초과할 수 없습니다' : ''}
                                </RegularText>
                            </Row>
                            <TextInput
                                autoFocus
                                value={partialPrice}
                                onChange={(value) => {
                                    // 숫자 검사는 입력 컴포넌트가 한다. 여기서는 값만 받는다.
                                    setPartialPrice(value);
                                    setIsPriceError(value > totalUnpaidAmount);
                                }}
                                comma
                                unit="원"
                                placeholder="0"
                                width={116}
                                error={isPriceError}
                            />
                        </Col>

                        <Col ref={divRef} className="flex flex-col">
                            <RegularText fontSize={16} lineHeight={23} color="#505967" className="mb-[12px]">
                                분할 결제 일시
                            </RegularText>
                            <Col
                                className={`flex  border  border-solid h-[46px] justify-center rounded-[10px] bg-white px-[18px] cursor-pointer hover:border-primary transition-colors duration-300 ease-in-out ${
                                    isCalendarOpen ? 'border-primary' : 'border-[#D9D9D9B2]'
                                }`}
                                onClick={() => {
                                    setIsCalendarOpen(true);
                                }}
                            >
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {dayjs(partialDate).format('YYYY-MM-DD')}
                                </RegularText>
                            </Col>
                        </Col>
                    </Col>
                </Row>
                <Col
                    className={`flex justify-center items-center self-end w-[214px] h-[71px]  bg-primary rounded-[9px] 
                        ${isPriceError || !partialPrice ? 'opacity-50 cursor-not-allowed' : ' cursor-pointer'}
                        `}
                    onClick={() => void submitPartialPayment()}
                >
                    <RegularText fontSize={20} lineHeight={23} color="white">
                        분할 결제하기
                    </RegularText>
                </Col>
            </Col>
        </Modal>
    );
};

export default ModalPartialPayment;

